import { Mail } from "lucide-react";
import type { AgentPulseLeaderboardEntry } from "@forgepoint/shared";

const DIRECTION_ARROW: Record<string, string> = {
  up: "▲",
  down: "▼",
  flat: "—",
};

const DIRECTION_COLOR: Record<string, string> = {
  up: "text-emerald-600",
  down: "text-red-500",
  flat: "text-muted-foreground",
};

interface LeaderboardListProps {
  entries: AgentPulseLeaderboardEntry[];
}

/**
 * Renders the ranked list of leaderboard entries with trend indicators
 * and anomaly badges.
 * @param props - The leaderboard entries to display
 * @returns The ranked list
 */
export function LeaderboardList({ entries }: LeaderboardListProps) {
  const maxCount = Math.max(...entries.map((e) => e.messageCount), 1);

  return (
    <div className="flex flex-col">
      {entries.map((entry, index) => {
        const rank = index + 1;
        const isTop3 = rank <= 3;
        const ratio = entry.messageCount / maxCount;
        const isAnomaly = ratio >= 0.75 && rank === 1 && entries.length > 2;
        const direction = inferDirection(rank, entries.length);

        return (
          <div
            key={entry.userId}
            className="flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-muted/40"
          >
            <span
              className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md font-mono text-[11px] font-semibold ${
                isTop3
                  ? "border border-foreground bg-foreground text-background"
                  : "border bg-muted/30 text-muted-foreground"
              }`}
            >
              {rank}
            </span>

            <span className="flex min-w-0 flex-1 items-center gap-2 text-[13.5px] font-medium">
              {entry.userName}
              {isAnomaly && (
                <span className="rounded-[5px] border border-amber-500/30 bg-amber-50 px-1.5 py-px font-mono text-[9px] font-semibold text-amber-600">
                  3x avg
                </span>
              )}
            </span>

            <span className="flex shrink-0 items-center gap-2 font-mono text-[13px] tabular-nums text-muted-foreground">
              <span className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                <b className="font-semibold text-foreground">
                  {entry.messageCount}
                </b>
              </span>
              <span
                className={`w-3 text-center text-[11px] ${DIRECTION_COLOR[direction]}`}
              >
                {DIRECTION_ARROW[direction]}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Infers trend direction based on rank position.
 * Placeholder logic until backend provides actual trend data.
 * @param rank - User's rank position
 * @param total - Total number of entries
 * @returns Inferred direction
 */
function inferDirection(rank: number, total: number): "up" | "down" | "flat" {
  if (rank <= Math.ceil(total * 0.4)) {
    return "up";
  }
  if (rank >= Math.ceil(total * 0.8)) {
    return "down";
  }
  return "flat";
}
